/**
 * RichTextEditor — a lightweight rich-text body editor for email composition
 * (Bob 2026-06-08: "there are not controls for RTF in the body of the email").
 *
 * A `contentEditable` surface with a small formatting toolbar (bold / italic /
 * underline / bullet + numbered lists / link). It reports BOTH the HTML
 * (`onChange` first arg → sent as `body_html`) and the plain-text rendering
 * (second arg → kept as the `body` fallback for non-HTML mail clients).
 *
 * The editor is intentionally uncontrolled internally (React does not own the
 * DOM selection) to avoid the caret-jump that controlled contentEditable
 * suffers; the parent seeds/clears it via `resetSignal`.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

interface RichTextEditorProps {
  /** Initial HTML to seed the editor when it mounts / resets. */
  initialHtml?: string;
  /** Fires on every edit with (html, plainText). */
  onChange: (html: string, plainText: string) => void;
  placeholder?: string;
  /** Bump this (e.g. a counter) to clear/reseed the editor — used on dialog open. */
  resetSignal?: number;
}

const TOOLS: {
  cmd: string;
  icon: typeof Bold;
  label: string;
  arg?: string;
  prompt?: string;
}[] = [
  { cmd: "bold", icon: Bold, label: "Bold" },
  { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "underline", icon: Underline, label: "Underline" },
  { cmd: "insertUnorderedList", icon: List, label: "Bulleted list" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
  { cmd: "createLink", icon: Link2, label: "Insert link", prompt: "Link URL:" },
];

export function RichTextEditor({
  initialHtml = "",
  onChange,
  placeholder,
  resetSignal,
}: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(!initialHtml);

  // Seed / clear the DOM when the reset signal changes (dialog open).
  useEffect(() => {
    if (ref.current) {
      // Security (audit r3, 2026-06-10): initialHtml is seeded from user-controlled
      // entity fields (titles/descriptions) via compose-email prefill, so this is a
      // stored-XSS sink (event-handler payloads like <img onerror> execute via
      // innerHTML). Run it through the same DOMPurify wrapper every other HTML sink
      // uses. Benign formatting (bold/list/link) is preserved; the toolbar mutates
      // the live DOM after seeding, so editing behavior is unaffected.
      ref.current.innerHTML = sanitizeHtml(initialHtml);
      setIsEmpty(!ref.current.textContent?.trim());
    }
    // Only reseed on an explicit reset, not on every initialHtml identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsEmpty(!el.textContent?.trim());
    // innerText is the better plain-text rendering (honors line breaks) but is
    // not implemented in jsdom; fall back to textContent so the plain-text
    // body is populated in both the browser and the test environment.
    const plain = el.innerText || el.textContent || "";
    onChange(el.innerHTML, plain);
  }, [onChange]);

  const exec = (cmd: string, prompt?: string) => {
    let arg: string | undefined;
    if (prompt) {
      const url = window.prompt(prompt, "https://");
      if (!url) return;
      arg = url;
    }
    ref.current?.focus();
    // execCommand is deprecated but remains the only cross-browser API for a
    // toolbar over contentEditable without pulling in a heavy editor library.
    document.execCommand(cmd, false, arg);
    emit();
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500 transition-all">
      {/* Toolbar */}
      <div
        className="flex items-center gap-0.5 border-b border-slate-100 dark:border-slate-800 px-1.5 py-1"
        data-testid="rte-toolbar"
      >
        {TOOLS.map((t) => (
          <button
            key={t.cmd}
            type="button"
            aria-label={t.label}
            title={t.label}
            data-testid={`rte-${t.cmd}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(t.cmd, t.prompt)}
            className="flex h-7 w-7 items-center justify-center rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          >
            <t.icon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>

      {/* Editable surface */}
      <div className="relative">
        {isEmpty && placeholder && (
          <span
            className="pointer-events-none absolute left-3 top-2 text-sm text-slate-500 dark:text-slate-400"
            aria-hidden="true"
          >
            {placeholder}
          </span>
        )}
        <div
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder || "Email body"}
          contentEditable
          suppressContentEditableWarning
          data-testid="compose-body-editor"
          onInput={emit}
          onBlur={emit}
          className={cn(
            "min-h-[10rem] w-full px-3 py-2 text-sm text-slate-900 dark:text-slate-100 outline-none",
            "prose prose-sm max-w-none dark:prose-invert [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5",
          )}
        />
      </div>
    </div>
  );
}

export default RichTextEditor;
